<script setup lang="tsx">
import { ref, computed, onMounted, watch } from 'vue'
import lupa from '@/assets/icons/search/lupa.svg'
import TextView from '@/ui/common/text-view/TextView.vue'
import { Tabs } from '@/ui/common/history/tabs'
import { AllTabContent } from '@/ui/common/history/all-tab-content'
import { ChatHistoryTab } from '@/ui/common/history/tabs'
import { ScreenSize, useScreenSize } from '@/composables/use-screen-size'
import { ITextViewVariant } from '@/ui/common/text-view/types'
import { useQueryParams } from '@/composables/use-query-params'
import { useAgentsLibraryStore } from '@/store/task-library'
import { storeToRefs } from 'pinia'
import { IThreadListRequestQuery } from '@/api/modules/threads/types'
import { useI18n } from 'vue-i18n'
import {useDebounceFn} from "@vueuse/core/index";

const { t, locale } = useI18n()

const { screenSize } = useScreenSize()

const selectedTab = ref<ChatHistoryTab>(ChatHistoryTab.All)

const { params, updateQueryParams } = useQueryParams<IThreadListRequestQuery>()

const agentsLibraryStore = useAgentsLibraryStore()

const { getCategoryItems } = agentsLibraryStore

const { agentCategoryItems } = storeToRefs(agentsLibraryStore)

const tabsData = computed(() => {
  return {
    [ChatHistoryTab.All]: t('page.history-chats-all'),
    ...agentCategoryItems.value.reduce(
      (p, c) => ({
        ...p,
        [c.code]: c.label,
      }),
      {},
    ),
  }
})

const search = ref('')

const loading = ref(false)

const headerVariant = computed((): ITextViewVariant => {
  switch (screenSize.value) {
    case ScreenSize.MOBILE:
      return 'roboto_28_bold'
    case ScreenSize.TABLET:
    case ScreenSize.DESKTOP_SMALL:
    case ScreenSize.DESKTOP_XL:
      return 'roboto_32_bold'
    case ScreenSize.DESKTOP_LARGE:
    default:
      return 'roboto_36_bold'
  }
})

const whenTabSelect = async (tab: string) => {
  loading.value = true
  if (tab != ChatHistoryTab.All) {
    updateQueryParams({ category: [tab], search: search.value, page: 1 })
  } else {
    updateQueryParams({ category: undefined, search: search.value, page: 1 })
  }
  selectedTab.value = tab as ChatHistoryTab

  setTimeout(() => {
    loading.value = false
  }, 100)
}

const searchFetch = () => {
  loading.value = true
  updateQueryParams({
    ...params.value,
    search: search.value,
    page: 1,
  })

  setTimeout(() => {
    loading.value = false
  }, 100)
}

const handleKeyDown = async ({ key }: KeyboardEvent) => key === 'Enter' && searchFetch()

onMounted(async () => {
  loading.value = true
  search.value = params.value.search || ''
  selectedTab.value = params.value.category || ChatHistoryTab.All

  await getCategoryItems()
  loading.value = false
})

watch(
  () => locale.value,
  async () => {
    loading.value = true
    updateQueryParams({ category: undefined, search: search.value, page: 1 })
    await getCategoryItems()
    loading.value = false
  },
)

const debouncedSearch = useDebounceFn(searchFetch, 500)

watch(() => search.value, debouncedSearch)
</script>

<template>
  <div class="HistoryPage">
    <div class="HistoryPage__wrapper">
      <TextView :variant="headerVariant" class="HistoryPage__header">{{
        t('page.history-chats-title')
      }}</TextView>

      <TextView variant="roboto_14_regular" color="gray_60" class="HistoryPage__label">
        {{ t('page.history-chats-text') }}
      </TextView>

      <a-input
        v-model:value="search"
        :placeholder="t('ui.search')"
        class="HistoryPage__input"
        size="large"
        @keydown="handleKeyDown"
      >
        <!--TODO зарефакторить и объединить в один компонент-->
        <template #suffix>
          <img :src="lupa" alt="Лупа" @click="searchFetch" class="HistoryPage__lupa" />
        </template>
      </a-input>
      <Tabs
        :selectedTab="selectedTab"
        :whenTabSelect="whenTabSelect"
        :tabs="tabsData"
        class="HistoryPage__tabs"
        :is-disabled="loading"
      />

      <div class="HistoryPage__content">
        <AllTabContent :loading="loading" />
      </div>
    </div>
  </div>
</template>
<style lang="css">
.HistoryPage {
  height: 100%;
  min-height: calc(100vh - 56px);
  background-color: var(--gray_0);
  flex-grow: 1;
}

.HistoryPage__wrapper {
  display: flex;
  margin: 0 auto;
  max-width: 808px;
  padding-bottom: 56px;
  align-items: center;
  align-content: start;
  height: 100%;
  flex-direction: column;
}

.HistoryPage__label {
  text-align: center;
  margin-bottom: 32px;
}

.HistoryPage__header {
  margin-bottom: 16px;
}

.HistoryPage__tabs {
  width: 100%;
  margin-bottom: 24px;
}

.HistoryPage__input {
  margin-bottom: 90px;
}

.HistoryPage__lupa {
  cursor: pointer;
}

.HistoryPage__content {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
}

.HistoryPage__spinner {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

@media (max-width: 767px) {
  .HistoryPage {
    padding: 48px 24px;
  }

  .HistoryPage__header {
    margin-top: 48px;
    margin-bottom: 12px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .HistoryPage {
    padding: 0 40px 0px 40px;
  }

  .HistoryPage__header {
    margin-top: 40px;
  }
}

@media (min-width: 1024px) and (max-width: 1279px) {
  .HistoryPage {
    padding: 0 84px 0px 84px;
  }

  .HistoryPage__header {
    margin-top: 56px;
  }
}

/* Desktop Large */
@media (min-width: 1280px) and (max-width: 1920px) {
  .HistoryPage {
    padding: 0 164px 0px 164px;
  }

  .HistoryPage__header {
    margin-top: 60px;
  }
}

@media (min-width: 1920px) {
  .HistoryPage__header {
    margin-top: 80px;
  }
}
</style>
